/**
 * EL PIN DEL PROPIETARIO, AL FINAL DEL CUESTIONARIO (ONB-PIN-CHAT-T1).
 *
 * AUDIENCIA: el cliente nuevo, desde el enlace publico que le llega. Casi
 * siempre en el celular y sin nadie de Nexo al lado.
 *
 * DE DONDE SALE. Decision de Josse del 14-sep: el cuestionario del panel
 * vincula negocio y propietario y genera el PIN; WhatsApp no maneja datos
 * sensibles. Con ese PIN y su cedula el propietario entra al programa.
 *
 * POR QUE VIVE APARTE DE LA VISTA. La vista guarda TODO lo que se contesta en
 * `captured`, lo copia al borrador del navegador y lo manda a responses. Un PIN
 * que pase por ahi queda en claro en tres lugares: el navegador, la sesion de
 * onboarding y la ficha del negocio. Aqui el PIN nunca sale de una variable
 * local de esta conversacion, y la vista solo recibe mensajes para pintar.
 * Eso es lo que permite probarlo sin navegador: el guardia conversa con esta
 * maquina y revisa que ningun mensaje, ningun estado y ningun cuerpo lleve el PIN
 * salvo el unico viaje permitido.
 *
 * EL CONTRATO ES DE ARGY-A (#14931), no de esta vista:
 *   POST /api/v1/onboarding/pin  {token, pin}
 *   204 fijado | 400 no sirve | 403 sin consentimiento | 404 token |
 *   409 sesion sin completar | 429 demasiados intentos (Retry-After)
 *
 * SE ENCIENDE SOLO. El 15-sep ese endpoint todavia no existe (404, con control
 * de misma forma tambien en 404). Antes de pedir el PIN se pregunta si ya esta
 * servido; mientras de 404, el chat cierra con el texto de siempre y no le pide
 * al cliente un dato que no se puede guardar.
 */

/** 'auto' pregunta si el endpoint existe; 'apagado' nunca pide el PIN. */
export const PIN_ONBOARD = 'auto';

export const PREGUNTA_PIN =
  'Último paso 🔐 Crea tu PIN de 4 dígitos para entrar al programa.\nNo lo compartas con nadie: ni siquiera nosotros te lo vamos a pedir.';
export const PREGUNTA_REPETIR = 'Repítelo, para estar seguros.';
export const ECO_PIN = '****';

/** El texto de cierre de siempre: se usa cuando no se pide PIN. */
export const CIERRE_SIN_PIN =
  '¡Listo! 🎉 Ya tengo todo lo básico de tu negocio. Un asesor de Nexo revisará tus datos y te contactamos para activar tu cuenta. Puedes cerrar esta ventana.';

export const CIERRE_CON_PIN =
  '¡Listo! 🎉 Tu PIN quedó guardado. Cuando instales el programa, entras con tu número de cédula y ese PIN. Un asesor de Nexo revisará tus datos y te contactamos para activar tu cuenta. Puedes cerrar esta ventana.';

/** Cuatro digitos y nada mas. La regla de PIN debil es del API, no se copia aqui. */
export function tieneFormaDePin(texto) {
  return /^[0-9]{4}$/.test(String(texto === null || texto === undefined ? '' : texto));
}

/** El unico cuerpo que lleva el PIN. Dos claves, en camelCase, sin nada mas. */
export function cuerpoDelPin(token, pin) {
  return { token: String(token), pin: String(pin) };
}

/**
 * ¿La sesion quedo completa? Se lee de la respuesta de POST responses.
 * El API contesta {data:{token, status, justCompleted}}. justCompleted es
 * false si la sesion YA estaba completa (el cliente que recarga el enlace), asi
 * que manda status; justCompleted solo suma.
 */
export function sesionCompleta(respuesta) {
  const d = respuesta && (respuesta.data || respuesta.Data) ? (respuesta.data || respuesta.Data) : respuesta;
  if (!d || typeof d !== 'object') return false;
  const status = String(d.status || d.Status || '').toLowerCase();
  return status === 'completed' || d.justCompleted === true || d.JustCompleted === true;
}

/**
 * ¿Ya existe el endpoint? Se pregunta con un cuerpo vacio: servido contesta
 * 400 (token vacio); sin servir, 404. Sin red (0) no se puede saber y no se
 * pide un PIN que no se va a poder guardar.
 */
export function pinPublicadoSegun(status) {
  // SOLO 400. Q lo cazo (#14987): con "todo lo que no sea 404" un API caido
  // (500, 502, 503) o un proxy que pide login (401) encendia el PIN sobre un
  // endpoint que no iba a guardar nada. A confirmo (#14992) que el cuerpo vacio
  // contesta 400 TOKEN_REQUERIDO sin tocar la base.
  return Number(status) === 400;
}

/** Fallas seguidas de servidor o de red antes de dejar de pedir el PIN. */
export const FALLAS_ANTES_DE_CERRAR = 2;

// El cierre por tope dice la causa que HUBO: sin red no es lo mismo que un
// servidor que contesta con error (Q #15030). La mitad que tranquiliza es igual.
const DATOS_QUEDARON = ' Tus datos del negocio sí quedaron guardados: un asesor de Nexo te contacta para crear tu PIN. Puedes cerrar esta ventana.';
export const CIERRE_PIN_FALLO_RED = 'No pude guardar tu PIN porque no hay conexión.' + DATOS_QUEDARON;
export const CIERRE_PIN_FALLO_SERVIDOR = 'No pude guardar tu PIN por un problema de nuestro lado.' + DATOS_QUEDARON;

/**
 * Lo que el chat hace con cada respuesta de POST pin. `repreguntar` dice si
 * vuelve a pedir el PIN o si la conversacion termina ahi.
 */
export function leerRespuestaDelPin(status, cuerpo, retryAfter) {
  const s = Number(status) || 0;
  // A (#14969): cada rechazo trae `code` y el chat decide POR CODE; el detail es
  // texto para humanos. Sin code (una version vieja) se cae al status.
  const code = String((cuerpo && (cuerpo.code || cuerpo.Code)) || '').toUpperCase();
  if (s === 204 || s === 200) return { estado: 'fijado', repreguntar: false, mensaje: CIERRE_CON_PIN };
  if (s === 400 && code === 'TOKEN_REQUERIDO') return {
    estado: 'token', repreguntar: false,
    mensaje: 'Este enlace ya no es válido, así que no pude guardar tu PIN. Tus datos sí quedaron: un asesor de Nexo te contacta.'
  };
  if (s === 400) {
    const debil = code === 'PIN_DEBIL';
    return {
      estado: debil ? 'debil' : 'no-sirve', repreguntar: true,
      mensaje: debil
        ? 'Ese PIN es muy fácil de adivinar (como 0000 o 1234). Elige otro.'
        : 'Ese PIN no sirve. Usa 4 números que no sean todos iguales ni seguidos (como 0000 o 1234).'
    };
  }
  if (s === 403) return {
    estado: 'sin-consentimiento', repreguntar: false,
    mensaje: 'No puedo guardar tu PIN porque falta tu autorización para tratar tus datos. Vuelve a abrir el enlace y responde SÍ a esa pregunta.'
  };
  // 410 ENLACE_VENCIDO (nexo-api 723, Ares #40212): el enlace vence a los 14 dias de creado; los datos ya quedaron.
  if (s === 410) return {
    estado: 'token', repreguntar: false,
    mensaje: 'Este enlace ya venció, así que no pude guardar tu PIN. Tus datos sí quedaron: pide un enlace nuevo a Nexo.'
  };
  if (s === 404) return {
    estado: 'token', repreguntar: false,
    mensaje: 'Este enlace ya no es válido, así que no pude guardar tu PIN. Tus datos sí quedaron: un asesor de Nexo te contacta.'
  };
  // A (#14992): el PIN se fija UNA vez. Si ya habia, 409 PIN_YA_FIJADO. No es un
  // error para el cliente: pasa tambien cuando el primer 204 se perdio por la red.
  if (s === 409 && code === 'PIN_YA_FIJADO') return {
    estado: 'ya-fijado', repreguntar: false,
    mensaje: 'Tu PIN ya había quedado guardado. Cuando instales el programa, entras con tu número de cédula y ese PIN. Si lo quieres cambiar, lo hace el administrador de tu negocio.'
  };
  if (s === 409) return {
    estado: 'sin-completar', repreguntar: false,
    mensaje: 'Todavía no terminé de guardar tus respuestas, así que el PIN no quedó. Un asesor de Nexo te contacta para terminar.'
  };
  if (s === 429) {
    const seg = Number(retryAfter);
    const cuando = seg > 0 ? ('en ' + Math.max(1, Math.ceil(seg / 60)) + ' minuto' + (Math.ceil(seg / 60) > 1 ? 's' : '')) : 'en unos minutos';
    return {
      estado: 'muchos-intentos', repreguntar: false,
      mensaje: 'Fueron muchos intentos seguidos. Vuelve a abrir este enlace ' + cuando + ' y crea tu PIN.'
    };
  }
  if (s === 0) return {
    estado: 'sin-red', repreguntar: true,
    mensaje: 'No pude guardar tu PIN: parece que no hay conexión. Intentémoslo otra vez.'
  };
  return {
    estado: 'error', repreguntar: true,
    mensaje: 'No pude guardar tu PIN por un problema de nuestro lado. Intentémoslo otra vez.'
  };
}

/**
 * La conversacion del PIN. `enviar(cuerpo)` hace el POST y devuelve
 * {status, cuerpo, retryAfter}; la vista lo implementa con fetch y el guardia
 * con uno de mentira.
 *
 * Devuelve mensajes para pintar y el tipo de caja que toca. El PIN vive en
 * `primero` mientras se espera la repeticion y se borra antes de enviarlo.
 */
export function crearConversacionDelPin({ token, enviar }) {
  let fase = 'crear';
  let primero = null;
  let fallasSeguidas = 0;

  function inicio() {
    fase = 'crear';
    primero = null;
    return { mensajes: [{ quien: 'argemiro', texto: PREGUNTA_PIN }], caja: 'pin', fase };
  }

  async function responder(texto) {
    const mensajes = [{ quien: 'me', texto: ECO_PIN }];
    if (fase === 'hecho' || fase === 'enviando') return { mensajes: [], caja: fase === 'hecho' ? 'cerrada' : 'pin', fase };

    if (fase === 'crear') {
      if (!tieneFormaDePin(texto)) {
        mensajes.push({ quien: 'argemiro', texto: 'Tienen que ser 4 números, sin letras ni espacios. Escríbelo otra vez.' });
        return { mensajes, caja: 'pin', fase };
      }
      primero = String(texto);
      fase = 'repetir';
      mensajes.push({ quien: 'argemiro', texto: PREGUNTA_REPETIR });
      return { mensajes, caja: 'pin', fase };
    }

    // fase 'repetir'
    const pin = String(texto);
    const coincide = pin === primero;
    primero = null;
    if (!coincide) {
      fase = 'crear';
      mensajes.push({ quien: 'argemiro', texto: 'No coinciden. Empecemos de nuevo: crea tu PIN de 4 dígitos.' });
      return { mensajes, caja: 'pin', fase };
    }

    fase = 'enviando';
    let r;
    try {
      r = await enviar(cuerpoDelPin(token, pin));
    } catch (e) {
      r = { status: 0 };
    }
    const lectura = leerRespuestaDelPin(r && r.status, r && r.cuerpo, r && r.retryAfter);
    // Un servidor caido o sin red NO se reintenta sin tope: el cliente esta solo y
    // en el celular, y sin salida solo le queda cerrar la ventana sin saber que
    // sus datos si quedaron (Q #14987).
    const fallaDeLinea = lectura.estado === 'error' || lectura.estado === 'sin-red';
    fallasSeguidas = fallaDeLinea ? fallasSeguidas + 1 : 0;
    if (fallaDeLinea && fallasSeguidas >= FALLAS_ANTES_DE_CERRAR) {
      fase = 'hecho';
      mensajes.push({ quien: 'argemiro', texto: lectura.estado === 'sin-red' ? CIERRE_PIN_FALLO_RED : CIERRE_PIN_FALLO_SERVIDOR });
      return { mensajes, caja: 'cerrada', fase, estado: 'sin-salida' };
    }
    mensajes.push({ quien: 'argemiro', texto: lectura.mensaje });
    if (lectura.repreguntar) {
      fase = 'crear';
      mensajes.push({ quien: 'argemiro', texto: PREGUNTA_PIN });
      return { mensajes, caja: 'pin', fase, estado: lectura.estado };
    }
    fase = 'hecho';
    return { mensajes, caja: 'cerrada', fase, estado: lectura.estado };
  }

  return { inicio, responder, fase: () => fase };
}
